import {useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {FieldError} from '@/components/field-error'
import {ListEmptyState} from '@/components/section-card'
import {TrazabilidadSwitch, useTrazabilidad} from '@/components/perfil/provenance-note'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {PorQue} from '@/components/ui/por-que'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {
  REQUISITOS_POR_PAGINA,
  useConfirmarRequisito,
  useQuitarRequisito,
  useRequisitosCargo,
  type RequisitoCargo,
} from '@/hooks/empresa/perfil_cargo/use-requisitos'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {formatDateOnly} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

/** `sin_periodicidad` en neutro: en ámbar se leería como un vencimiento que nadie calculó. */
const TONOS = {verde: 'green', amarillo: 'amber', rojo: 'red', sin_periodicidad: 'neutral'} as const

type Color = keyof typeof TONOS

/** Condición → norma → requisito; la clave del renglón de la norma es su estado de verificación. */
function Porque({requisito}: {requisito: RequisitoCargo}) {
  const {t} = useTranslation()
  return (
    <ol className="mt-1 space-y-0.5 text-xs text-muted-foreground" data-testid={`requisito-porque-${requisito.nombre}`}>
      {requisito.porque.map((renglon, indice) => (
        <li key={`${renglon.clave}-${indice}`}>
          {t(`requisitoCargo.porque.${renglon.clave}`, {
            valor: renglon.valor,
            articulo: requisito.norma_articulo ?? '',
          })}
        </li>
      ))}
    </ol>
  )
}

/** El color nunca sale sin su base: la cifra de la empresa y la de la norma no se auditan igual. */
function Reloj({requisito}: {requisito: RequisitoCargo}) {
  const {t} = useTranslation()
  const color = requisito.reloj.color as Color
  const clave = requisito.reloj.vence_el ? `${requisito.reloj.base}_vence` : requisito.reloj.base
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`requisito-reloj-${requisito.nombre}`}>
      <Tag tone={TONOS[color]}>{t(`requisitoCargo.reloj.${color}`)}</Tag>
      {requisito.reloj.base !== 'sin_verificar' && (
        <span className="text-xs text-muted-foreground">
          {t(`requisitoCargo.base.${clave}`, {
            meses: requisito.reloj.meses ?? '',
            fecha: requisito.reloj.vence_el ? formatDateOnly(requisito.reloj.vence_el) : '',
          })}
        </span>
      )}
    </div>
  )
}

function EstadoDeLaFila({requisito, perfilHref}: {requisito: RequisitoCargo; perfilHref: string}) {
  const {t} = useTranslation()
  if (requisito.no_exigible_desde) {
    return (
      <p className="text-xs text-muted-foreground" data-testid={`requisito-quitado-${requisito.nombre}`}>
        {t('requisitoCargo.quitado', {
          fecha: formatDateOnly(requisito.no_exigible_desde),
          motivo: requisito.quitado_motivo ?? '',
        })}
      </p>
    )
  }
  if (requisito.id === null) {
    return (
      <p className="text-xs text-muted-foreground" data-testid={`requisito-por-nacer-${requisito.nombre}`}>
        {t('requisitoCargo.porNacer')}{' '}
        <Link className="underline" to={perfilHref}>
          {t('requisitoCargo.irAConfirmarElPerfil')}
        </Link>
      </p>
    )
  }
  return null
}

function Rotulos({requisito, desplegado}: {requisito: RequisitoCargo; desplegado: boolean}) {
  const {t} = useTranslation()
  return (
    <div className="flex flex-wrap gap-2">
      {requisito.origen === 'estandar_minimo' && (
        <Tag tone="neutral" data-testid={`requisito-estandar-${requisito.nombre}`}>
          {t('requisitoCargo.rotulo.estandarMinimo')}
        </Tag>
      )}
      {/* Neutral, no lime: el lime es el escalón bajo de la rampa de riesgo (U2). */}
      {requisito.apretado && (
        <>
          <Tag tone="neutral" data-testid={`requisito-apretado-${requisito.nombre}`}>
            {t('requisitoCargo.rotulo.apretadoTag')}
          </Tag>
          {/* Las filas apretadas antes de guardar quién y cuándo solo dicen el motivo. */}
          {desplegado && (
            <PorQue
              label={t('common.deDondeSalio')}
              titulo={t('common.deDondeSalio')}
              ariaLabel={`${t('common.deDondeSalio')} — ${requisito.nombre}`}
              contenidoTestId={`requisito-apretado-detalle-${requisito.nombre}`}
              siempreVisible>
              <p>
                {requisito.apretado_por_nombre
                  ? t('requisitoCargo.rotulo.apretadoPor', {
                      motivo: requisito.apretado_motivo ?? '',
                      nombre: requisito.apretado_por_nombre,
                      fecha: requisito.apretado_en ? formatDateOnly(requisito.apretado_en) : '',
                    })
                  : t('requisitoCargo.rotulo.apretado', {motivo: requisito.apretado_motivo ?? ''})}
              </p>
            </PorQue>
          )}
        </>
      )}
    </div>
  )
}

function QuitarDialog({
  requisito,
  onCerrar,
  onQuitar,
  error,
}: {
  requisito: RequisitoCargo
  onCerrar: () => void
  onQuitar: (motivo: string) => void
  error: string | null
}) {
  const {t} = useTranslation()
  const [motivo, setMotivo] = useState('')
  const [invalido, setInvalido] = useState<string | null>(null)
  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('requisitoCargo.quitarTitulo', {nombre: requisito.nombre})}</DialogTitle>
          <DialogDescription>{t('requisitoCargo.quitarAviso')}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="space-y-3"
          onSubmit={(evento) => {
            evento.preventDefault()
            const fallo = validateRequired(motivo)
            setInvalido(fallo)
            if (!fallo) onQuitar(motivo)
          }}>
          <div className="space-y-1">
            <Label htmlFor="requisito-motivo">{t('requisitoCargo.motivo')}</Label>
            <Textarea
              maxLength={tope('NOTA')}
              id="requisito-motivo"
              value={motivo}
              onBlur={() => setInvalido(validateRequired(motivo))}
              onChange={(evento) => setMotivo(evento.target.value)}
            />
            <FieldError error={invalido ?? undefined} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" data-testid="requisito-quitar-confirmar">
            {t('requisitoCargo.quitar')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** E10-H05. Ninguna fila llega a verde sola. */
export function RequisitosCargo({
  businessId,
  positionId,
  canEdit,
  razonBloqueo,
}: {
  businessId: string
  positionId: string
  canEdit: boolean
  razonBloqueo: string | null
}) {
  const {t} = useTranslation()
  const perfilHref = `/businesses/${businessId}/sgsst/perfiles-cargo/${positionId}`
  const [page, setPage] = useState(1)
  const requisitos = useRequisitosCargo(businessId, positionId, page)
  const confirmar = useConfirmarRequisito(businessId, positionId)
  const quitar = useQuitarRequisito(businessId, positionId)
  const [quitando, setQuitando] = useState<RequisitoCargo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [trazabilidad, setTrazabilidad] = useTrazabilidad()

  if (requisitos.isLoading) return <Skeleton className="h-24 w-full" />
  // Un fallo de red no se pinta como vacío: afirmaría que los requisitos no existen.
  if (requisitos.isError) {
    return (
      <p className="text-sm text-destructive" data-testid="requisitos-error">
        {t('requisitoCargo.errorCarga')}
      </p>
    )
  }
  const items = requisitos.data?.items ?? []

  return (
    <section className="surface space-y-4 rounded-xl p-4" data-testid="requisitos-cargo">
      <div className="flex justify-end">
        <TrazabilidadSwitch desplegado={trazabilidad} onChange={setTrazabilidad} testId="requisitos-trazabilidad" />
      </div>
      {!canEdit && razonBloqueo && (
        <p className="text-xs text-muted-foreground" data-testid="requisitos-sin-firma">
          {razonBloqueo}
        </p>
      )}
      {items.length === 0 ? (
        <ListEmptyState
          title={t('requisitoCargo.vacio')}
          action={
            <Link className="text-sm underline" to={perfilHref} data-testid="requisitos-vacio">
              {t('requisitoCargo.irAConfirmarElPerfil')}
            </Link>
          }
        />
      ) : (
        <ul className="space-y-4">
          {items.map((requisito) => (
            <li
              key={requisito.id ?? requisito.nombre}
              className="space-y-2 border-t pt-3 first:border-t-0 first:pt-0"
              data-testid={`requisito-${requisito.nombre}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium">{requisito.nombre}</p>
                <Reloj requisito={requisito} />
              </div>
              <Rotulos requisito={requisito} desplegado={trazabilidad} />
              <Porque requisito={requisito} />
              <EstadoDeLaFila requisito={requisito} perfilHref={perfilHref} />
              {canEdit && requisito.id !== null && !requisito.no_exigible_desde && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    // Confirmar no se repite: el servidor devuelve 409 la segunda vez.
                    disabled={confirmar.isPending}
                    data-testid={`requisito-confirmar-${requisito.nombre}`}
                    onClick={() => {
                      setError(null)
                      confirmar.mutate(requisito.id!, {onError: (err) => setError(translateApiError(err))})
                    }}>
                    {t('requisitoCargo.confirmar')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`requisito-quitar-${requisito.nombre}`}
                    onClick={() => {
                      setError(null)
                      setQuitando(requisito)
                    }}>
                    {t('requisitoCargo.quitar')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {requisitos.data && requisitos.data.total > 0 && (
        <PaginationBar
          page={page}
          pageSize={REQUISITOS_POR_PAGINA}
          total={requisitos.data.total}
          onPageChange={setPage}
        />
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {quitando && (
        <QuitarDialog
          requisito={quitando}
          error={error}
          onCerrar={() => setQuitando(null)}
          onQuitar={(motivo) =>
            quitar.mutate(
              {requisitoId: quitando.id!, motivo},
              {onSuccess: () => setQuitando(null), onError: (err) => setError(translateApiError(err))}
            )
          }
        />
      )}
    </section>
  )
}
